import { forwardRef, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  Building2,
  CheckSquare,
  Clock,
  Copy,
  FileText,
  FolderKanban,
  Globe,
  Plus,
  Search,
  Send,
  Sparkles,
  User,
} from 'lucide-react';
import type {
  Client,
  Proposal,
  Task,
  User as UserEntity,
  Website,
} from '@oneup/shared';
import { clientsApi, proposalsApi, tasksApi, usersApi, websitesApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { readRecents, pushRecent, type RecentEntityType, type RecentItem } from '@/lib/recents';
import { useUiStore, type CreateDrawerInitial } from '@/stores/ui';
import { cn } from '@/lib/utils';
import { NAVIGATION_PLATFORM_ACTIONS } from '@/platform/platform-actions';
import { VaultSearchProvider } from '@/lib/vault-search-provider';

const QUICK_ACTIONS = ['/new task', '/new proposal', '/new client', '/new contact', '/new website', '/log time', '/today', '/send email', '/send update'];

// The `/goto` navigation commands are generated from the shared Platform Action
// registry (apps/web/src/platform/platform-actions.ts), so the command palette
// and Alice converge on one source of action definitions. Remaining bespoke
// commands below stay here until they are migrated onto the registry too.
const NAVIGATION_COMMANDS: Array<{ label: string; hint: string; run: (ctx: CommandContext) => void }> =
  NAVIGATION_PLATFORM_ACTIONS.filter((action) => action.paletteCommand && action.route).map(
    (action) => ({
      label: action.paletteCommand as string,
      hint: action.label,
      run: ({ navigate }: CommandContext) => navigate(action.route as string),
    }),
  );

const COMMANDS: Array<{ label: string; hint: string; run: (ctx: CommandContext) => void }> = [
  {
    label: '/new task',
    hint: 'Open Create Task drawer',
    run: ({ openCreateDrawer }) => openCreateDrawer({ type: 'task' }),
  },
  {
    label: '/log time',
    hint: 'Focus Quick Log for the active task',
    run: ({ activeDrawer, requestTaskDrawerCommand, setQuery, setNotice }) => {
      if (activeDrawer?.type === 'task') requestTaskDrawerCommand('quick-log');
      else {
        setNotice('Pick a task to log time');
        setQuery('/log time ');
      }
    },
  },
  {
    label: '/send email',
    hint: 'Open Gmail composer',
    run: ({ openEmailComposer }) => openEmailComposer(),
  },
  {
    label: '/send update',
    hint: 'Review update for the active task',
    run: ({ activeDrawer, requestTaskDrawerCommand, setNotice }) => {
      if (activeDrawer?.type === 'task') requestTaskDrawerCommand('send-update');
      else setNotice('Open a task before sending an update', 'destructive');
    },
  },
  {
    label: '/copy id',
    hint: 'Copy active task Airtable record id',
    run: ({ activeDrawer, setNotice }) => {
      if (activeDrawer?.type !== 'task') {
        setNotice('Open a task before copying its id', 'destructive');
        return;
      }
      navigator.clipboard.writeText(activeDrawer.id).then(
        () => setNotice('Task id copied'),
        () => setNotice('Could not copy task id', 'destructive'),
      );
    },
  },
  { label: '/today', hint: 'Go to Today', run: ({ navigate }) => navigate('/tasks?queue=today') },
  {
    label: '/queue closed',
    hint: 'Show closed tasks',
    run: ({ navigate }) => navigate('/tasks?queue=all&search=Closed'),
  },
  {
    label: '/queue waiting-on-client',
    hint: 'Show waiting-on-client queue',
    run: ({ navigate }) => navigate('/tasks?queue=waiting_on_client'),
  },
  {
    label: '/queue review',
    hint: 'Show review queue',
    run: ({ navigate }) => navigate('/tasks?queue=review'),
  },
  {
    label: '/queue ready-to-bill',
    hint: 'Show ready-to-bill queue',
    run: ({ navigate }) => navigate('/tasks?queue=ready_to_bill'),
  },
  {
    label: '/jump <client>',
    hint: 'Filter tasks by client',
    run: ({ setQuery }) => setQuery('/jump '),
  },
  ...NAVIGATION_COMMANDS,
  {
    label: '/new client',
    hint: 'Open Create Organization drawer',
    run: ({ openCreateDrawer }) => openCreateDrawer({ type: 'client' }),
  },
  {
    label: '/new contact',
    hint: 'Open Create Contact drawer',
    run: ({ openCreateDrawer }) => openCreateDrawer({ type: 'contact' }),
  },
  {
    label: '/new website',
    hint: 'Open Create Website drawer',
    run: ({ openCreateDrawer }) => openCreateDrawer({ type: 'website' }),
  },
  {
    label: '/new proposal',
    hint: 'Open Create Proposal drawer',
    run: ({ openCreateDrawer }) => openCreateDrawer({ type: 'proposal' }),
  },
  { label: '/proposals draft', hint: 'Show draft proposals', run: ({ navigate }) => navigate('/proposals?filter=draft') },
  { label: '/proposals needs pricing', hint: 'Show proposals needing pricing', run: ({ navigate }) => navigate('/proposals?filter=needs-pricing') },
];

interface CommandContext {
  navigate: (to: string) => void;
  setQuery: (query: string) => void;
  openCreateDrawer: (initial: CreateDrawerInitial) => void;
  openEmailComposer: () => void;
  activeDrawer: ReturnType<typeof useUiStore.getState>['activeDrawer'];
  requestTaskDrawerCommand: ReturnType<typeof useUiStore.getState>['requestTaskDrawerCommand'];
  setNotice: ReturnType<typeof useUiStore.getState>['setNotice'];
}

export function CommandPalette() {
  const navigate = useNavigate();
  const open = useUiStore((state) => state.commandPaletteOpen);
  const initialQuery = useUiStore((state) => state.commandPaletteInitialQuery);
  const setOpen = useUiStore((state) => state.setCommandPaletteOpen);
  const openCreateDrawer = useUiStore((state) => state.openCreateDrawer);
  const openEmailComposer = useUiStore((state) => state.openEmailComposer);
  const activeDrawer = useUiStore((state) => state.activeDrawer);
  const requestTaskDrawerCommand = useUiStore((state) => state.requestTaskDrawerCommand);
  const setNotice = useUiStore((state) => state.setNotice);
  const openCopilotPanel = useUiStore((state) => state.openCopilotPanel);
  const seedCopilotPanel = useUiStore((state) => state.seedCopilotPanel);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [recents, setRecents] = useState<RecentItem[]>([]);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const copilotInputRef = useRef<HTMLTextAreaElement>(null);

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'all'],
    queryFn: () => tasksApi.list({ limit: 300 }),
    enabled: open,
  });
  const clientsQuery = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => clientsApi.list(),
    enabled: open,
  });
  const usersQuery = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.list(),
    enabled: open,
  });
  const websitesQuery = useQuery({
    queryKey: ['websites', 'all'],
    queryFn: () => websitesApi.list(),
    enabled: open,
  });
  const proposalsQuery = useQuery({
    queryKey: ['proposals', 'all'],
    queryFn: () => proposalsApi.list({ limit: 500 }),
    enabled: open,
  });

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), 50);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    setQuery(initialQuery);
    setDebouncedQuery(initialQuery);
    setRecents(readRecents());
  }, [initialQuery, open]);

  useEffect(() => {
    const handle = () => setRecents(readRecents());
    window.addEventListener('cmdk:recents-updated', handle);
    return () => window.removeEventListener('cmdk:recents-updated', handle);
  }, []);

  const close = () => {
    setOpen(false);
    setTimeout(() => previousFocusRef.current?.focus(), 0);
  };

  const commandContext: CommandContext = {
    navigate: (to) => {
      navigate(to);
      close();
    },
    setQuery,
    openCreateDrawer: (initial) => {
      openCreateDrawer(initial);
      close();
    },
    openEmailComposer: () => {
      openEmailComposer({ mode: 'new' });
      close();
    },
    activeDrawer,
    requestTaskDrawerCommand: (command) => {
      requestTaskDrawerCommand(command);
      close();
    },
    setNotice,
  };

  const mode: 'search' | 'commands' | 'copilot' = debouncedQuery.startsWith('?')
    ? 'copilot'
    : debouncedQuery.startsWith('/')
      ? 'commands'
      : 'search';
  const trimmedSearchQuery = debouncedQuery.trim();
  const copilotPromptDraft = mode === 'copilot' ? debouncedQuery.slice(1).trimStart() : '';

  const launchCopilot = (prompt: string) => {
    const taskId = activeDrawer?.type === 'task' ? activeDrawer.id : undefined;
    const proposalId = activeDrawer?.type === 'proposal' ? activeDrawer.id : undefined;
    openCopilotPanel(taskId, proposalId);
    const trimmed = prompt.trim();
    if (trimmed.length > 0) seedCopilotPanel(trimmed);
    close();
  };

  // Focus the Copilot textarea when entering Copilot mode so the user can
  // keep typing without lifting hands off the keyboard.
  useEffect(() => {
    if (!open || mode !== 'copilot') return;
    const t = window.setTimeout(() => {
      const node = copilotInputRef.current;
      if (!node) return;
      node.focus();
      const len = node.value.length;
      node.setSelectionRange(len, len);
    }, 30);
    return () => window.clearTimeout(t);
  }, [open, mode]);

  const vaultDocsQuery = useQuery({
    queryKey: ['vault', 'palette-search', trimmedSearchQuery],
    queryFn: () => VaultSearchProvider(trimmedSearchQuery),
    enabled: open && mode === 'search' && trimmedSearchQuery.length > 0,
    staleTime: 15_000,
  });

  const searchResults = useSearchResults({
    query: debouncedQuery,
    tasks: tasksQuery.data?.items ?? [],
    clients: clientsQuery.data?.items ?? [],
    users: usersQuery.data?.items ?? [],
    websites: websitesQuery.data?.items ?? [],
    proposals: proposalsQuery.data?.items ?? [],
  });

  const slashText = debouncedQuery.slice(1).toLowerCase();
  const jumpText = debouncedQuery.startsWith('/jump ') ? debouncedQuery.slice('/jump '.length) : '';
  const logTimeText = debouncedQuery.startsWith('/log time ')
    ? debouncedQuery.slice('/log time '.length)
    : '';
  const matchedCommands = COMMANDS.filter((command) =>
    command.label.slice(1).toLowerCase().includes(slashText),
  );
  const jumpClients = filterClients(clientsQuery.data?.items ?? [], jumpText);
  const logTasks = filterTasks(tasksQuery.data?.items ?? [], logTimeText);
  const namedProposalShortcuts = findNamedProposalShortcuts(
    proposalsQuery.data?.items ?? [],
    recents,
  );

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) close();
      }}
      label="Command palette"
      className="fixed left-1/2 top-[max(12px,env(safe-area-inset-top))] z-[80] max-h-[calc(100vh-24px)] w-[calc(100vw-16px)] max-w-[760px] -translate-x-1/2 overflow-hidden rounded-xl border border-border/80 bg-popover/95 text-popover-foreground shadow-[0_24px_80px_rgba(15,23,42,0.24)] backdrop-blur sm:top-[11vh] sm:w-[min(760px,calc(100vw-32px))]"
    >
      <DialogTitle className="sr-only">Command palette</DialogTitle>
      <DialogDescription className="sr-only">
        Search records, run commands, or open create drawers.
      </DialogDescription>
      <div className="h-1 bg-gradient-to-r from-primary/70 via-primary/25 to-transparent" />
      <div className="m-2 flex items-center gap-2 rounded-lg border border-border bg-background/80 px-3 shadow-sm transition focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/10">
        <Search className="h-4 w-4 text-primary/70" aria-hidden />
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder="Search & Command"
          className="h-11 flex-1 bg-transparent text-[16px] outline-none placeholder:text-muted-foreground focus:outline-none focus:ring-0 sm:text-[14px]"
        />
      </div>
      <Command.List className="max-h-[min(72vh,540px)] overflow-y-auto px-2 pb-[max(12px,env(safe-area-inset-bottom))] pt-1 sm:max-h-[540px]">
        {mode !== 'copilot' && (
          <Command.Empty className="px-3 py-8 text-center text-sm text-muted-foreground">
            No results.
          </Command.Empty>
        )}

        {mode === 'search' && debouncedQuery.trim().length === 0 && (
          <>
            <Command.Group heading="Alice">
              <CommandRow
                value="copilot ask"
                icon={<Sparkles className="h-4 w-4" />}
                label="Ask Alice..."
                secondary="Type ? to start, or use the right panel"
                onSelect={() => launchCopilot('')}
              />
            </Command.Group>
            <RecentSection recents={recents} onSelect={(item) => openRecent(item, navigate, close)} />
            <Command.Group heading="Quick Actions" className="cmdk-group">
              {QUICK_ACTIONS.map((label) => {
                const command = COMMANDS.find((item) => item.label === label);
                if (!command) return null;
                return (
                  <CommandRow
                    key={label}
                    value={label}
                    icon={iconForCommand(label)}
                    label={label}
                    secondary={command.hint}
                    onSelect={() => command.run(commandContext)}
                  />
                );
              })}
            </Command.Group>
            {namedProposalShortcuts.length > 0 && (
              <Command.Group heading="Proposals">
                {namedProposalShortcuts.map((proposal) => (
                  <CommandRow
                    key={proposal.label}
                    value={proposal.label}
                    icon={<FileText className="h-4 w-4" />}
                    label={proposal.label}
                    secondary={proposal.secondary}
                    badge="proposal"
                    onSelect={() => {
                      pushRecent({
                        type: 'proposal',
                        id: proposal.id,
                        name: proposal.name,
                        secondary: proposal.secondary,
                      });
                      navigate(`/proposals/${proposal.id}`);
                      close();
                    }}
                  />
                ))}
              </Command.Group>
            )}
            <div className="border-t border-border/70 px-3 py-2 text-[11px] text-muted-foreground">
              Type / for commands, ? to ask Alice, or start typing to search
            </div>
          </>
        )}

        {mode === 'copilot' && (
          <CopilotPromptBlock
            ref={copilotInputRef}
            initialPrompt={copilotPromptDraft}
            onSubmit={(prompt) => launchCopilot(prompt)}
            taskContext={activeDrawer?.type === 'task' ? activeDrawer.name : undefined}
            proposalContext={activeDrawer?.type === 'proposal' ? activeDrawer.name : undefined}
          />
        )}

        {mode === 'search' && debouncedQuery.trim().length > 0 && (
          <>
            <ResultSection
              heading="Tasks"
              items={searchResults.tasks}
              render={(task) => (
                <CommandRow
                  value={`task ${task.name}`}
                  icon={<CheckSquare className="h-4 w-4" />}
                  label={task.name}
                  secondary={task.client?.name}
                  badge={task.status}
                  onSelect={() => {
                    pushRecent({
                      type: 'task',
                      id: task.id,
                      name: task.name,
                      secondary: task.client?.name,
                    });
                    navigate(`/tasks/${task.id}`);
                    close();
                  }}
                />
              )}
            />
            <ResultSection
              heading="Clients"
              items={searchResults.clients}
              render={(client) => (
                <CommandRow
                  value={`client ${client.name}`}
                  icon={<Building2 className="h-4 w-4" />}
                  label={client.name}
                  secondary={formatRate(client.hourlyRate)}
                  badge={client.status}
                  onSelect={() => {
                    pushRecent({ type: 'client', id: client.id, name: client.name, secondary: client.status });
                    navigate(`/clients/${client.id}`);
                    close();
                  }}
                />
              )}
            />
            <ResultSection
              heading="Projects"
              items={searchResults.projects}
              render={(project) => (
                <CommandRow
                  value={`project ${project.name} ${project.client?.name ?? ''}`}
                  icon={<FolderKanban className="h-4 w-4" />}
                  label={project.name}
                  secondary={project.client?.name}
                  badge={project.status}
                  onSelect={() => {
                    navigate(`/work/projects/${project.id}`);
                    close();
                  }}
                />
              )}
            />
            <ResultSection
              heading="Vault Docs"
              items={vaultDocsQuery.data ?? []}
              render={(doc) => (
                <CommandRow
                  value={`vault ${doc.title} ${doc.folder_path ?? ''} ${doc.snippet}`}
                  icon={<FileText className="h-4 w-4" />}
                  label={doc.title}
                  secondary={doc.folder_path ?? 'Unfiled'}
                  tertiary={doc.snippet}
                  badge={labelize(doc.doc_type)}
                  onSelect={() => {
                    navigate(
                      `/knowledge/md-vault?doc=${encodeURIComponent(doc.slug || doc.document_id)}&mode=viewer`,
                    );
                    close();
                  }}
                />
              )}
            />
            <Command.Group heading="AI">
              <CommandRow
                value={`ask alice ${trimmedSearchQuery}`}
                icon={<Sparkles className="h-4 w-4" />}
                label="Ask Alice..."
                secondary={trimmedSearchQuery}
                onSelect={() => launchCopilot(trimmedSearchQuery)}
              />
            </Command.Group>
            <ResultSection
              heading="Contacts"
              items={searchResults.users}
              render={(user) => (
                <CommandRow
                  value={`user ${user.fullName} ${user.email ?? ''}`}
                  icon={<User className="h-4 w-4" />}
                  label={user.fullName}
                  secondary={user.email}
                  badge={user.userTypes?.[0]}
                  onSelect={() => {
                    pushRecent({ type: 'user', id: user.id, name: user.fullName, secondary: user.email });
                    navigate(`/users/${user.id}`);
                    close();
                  }}
                />
              )}
            />
            <ResultSection
              heading="Websites"
              items={searchResults.websites}
              render={(website) => (
                <CommandRow
                  value={`website ${website.friendlyName ?? ''} ${website.url}`}
                  icon={<Globe className="h-4 w-4" />}
                  label={website.friendlyName ?? website.url}
                  secondary={website.url}
                  badge={website.status}
                  onSelect={() => {
                    pushRecent({
                      type: 'website',
                      id: website.id,
                      name: website.friendlyName ?? website.url,
                      secondary: website.url,
                    });
                    navigate(`/websites/${website.id}`);
                    close();
                  }}
                />
              )}
            />
            <ResultSection
              heading="Proposals"
              items={searchResults.proposals}
              render={(proposal) => (
                <CommandRow
                  value={`proposal ${proposal.proposalName}`}
                  icon={<FileText className="h-4 w-4" />}
                  label={proposal.proposalName}
                  secondary={proposal.proposalType ?? proposal.internalNextAction}
                  badge={proposal.status}
                  onSelect={() => {
                    pushRecent({
                      type: 'proposal',
                      id: proposal.id,
                      name: proposal.proposalName,
                      secondary: proposal.status,
                    });
                    navigate(`/proposals/${proposal.id}`);
                    close();
                  }}
                />
              )}
            />
          </>
        )}

        {mode === 'commands' && (
          <>
            {debouncedQuery.startsWith('/jump ') ? (
              <Command.Group heading="Jump To Client">
                {jumpClients.map((client) => (
                  <CommandRow
                    key={client.id}
                    value={`/jump ${client.name}`}
                    icon={<Building2 className="h-4 w-4" />}
                    label={client.name}
                    secondary="Filter tasks by this client"
                    onSelect={() => {
                      navigate(`/tasks?clientId=${encodeURIComponent(client.id)}`);
                      close();
                    }}
                  />
                ))}
              </Command.Group>
            ) : debouncedQuery.startsWith('/log time ') ? (
              <Command.Group heading="Pick Task">
                {logTasks.map((task) => (
                  <CommandRow
                    key={task.id}
                    value={`/log time ${task.name}`}
                    icon={<Clock className="h-4 w-4" />}
                    label={task.name}
                    secondary={task.client?.name}
                    onSelect={() => {
                      navigate(`/tasks/${task.id}`);
                      requestAnimationFrame(() => requestTaskDrawerCommand('quick-log'));
                      close();
                    }}
                  />
                ))}
              </Command.Group>
            ) : (
              <Command.Group heading="Commands">
                {matchedCommands.map((command) => (
                  <CommandRow
                    key={command.label}
                    value={command.label}
                    icon={iconForCommand(command.label)}
                    label={command.label}
                    secondary={command.hint}
                    onSelect={() => command.run(commandContext)}
                  />
                ))}
              </Command.Group>
            )}
          </>
        )}
      </Command.List>
    </Command.Dialog>
  );
}

function RecentSection({
  recents,
  onSelect,
}: {
  recents: RecentItem[];
  onSelect: (item: RecentItem) => void;
}) {
  if (recents.length === 0) return null;
  return (
    <Command.Group heading="Recents">
      {recents.map((item) => (
        <CommandRow
          key={`${item.type}:${item.id}`}
          value={`recent ${item.type} ${item.name}`}
          icon={iconForType(item.type)}
          label={item.name}
          secondary={item.secondary}
          badge={item.type}
          onSelect={() => onSelect(item)}
        />
      ))}
    </Command.Group>
  );
}

function ResultSection<T>({
  heading,
  items,
  render,
}: {
  heading: string;
  items: T[];
  render: (item: T) => ReactNode;
}) {
  if (items.length === 0) return null;
  return <Command.Group heading={heading}>{items.map(render)}</Command.Group>;
}

function CommandRow({
  value,
  icon,
  label,
  secondary,
  tertiary,
  badge,
  onSelect,
}: {
  value: string;
  icon: ReactNode;
  label: string;
  secondary?: string;
  tertiary?: string;
  badge?: string;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[13px]',
        'aria-selected:bg-accent aria-selected:text-accent-foreground',
      )}
    >
      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{label}</span>
        {secondary && <span className="block truncate text-[11px] text-muted-foreground">{secondary}</span>}
        {tertiary && <span className="block truncate text-[11px] text-muted-foreground">{tertiary}</span>}
      </span>
      {badge && <Badge variant="muted">{badge}</Badge>}
    </Command.Item>
  );
}

function useSearchResults({
  query,
  tasks,
  clients,
  users,
  websites,
  proposals,
}: {
  query: string;
  tasks: Task[];
  clients: Client[];
  users: UserEntity[];
  websites: Website[];
  proposals: Proposal[];
}) {
  return useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { tasks: [], clients: [], projects: [], users: [], websites: [], proposals: [] };
    return {
      tasks: tasks.filter((task) => task.name.toLowerCase().includes(q)).slice(0, 5),
      clients: clients.filter((client) => client.name.toLowerCase().includes(q)).slice(0, 5),
      projects: tasks
        .filter((task) => (task.childTaskIds?.length ?? 0) > 0)
        .filter(
          (task) =>
            task.name.toLowerCase().includes(q) ||
            (task.client?.name ?? '').toLowerCase().includes(q),
        )
        .slice(0, 5),
      users: users
        .filter(
          (user) =>
            user.fullName.toLowerCase().includes(q) || (user.email ?? '').toLowerCase().includes(q),
        )
        .slice(0, 5),
      websites: websites
        .filter(
          (website) =>
            (website.friendlyName ?? '').toLowerCase().includes(q) ||
            (website.url ?? '').toLowerCase().includes(q),
        )
        .slice(0, 5),
      proposals: proposals
        .filter(
          (proposal) =>
            proposal.proposalName.toLowerCase().includes(q) ||
            (proposal.scopeSummary ?? '').toLowerCase().includes(q) ||
            (proposal.internalNextAction ?? '').toLowerCase().includes(q),
        )
        .slice(0, 5),
    };
  }, [clients, proposals, query, tasks, users, websites]);
}

function filterClients(clients: Client[], query: string) {
  const q = query.trim().toLowerCase();
  return clients.filter((client) => !q || client.name.toLowerCase().includes(q)).slice(0, 8);
}

function filterTasks(tasks: Task[], query: string) {
  const q = query.trim().toLowerCase();
  return tasks.filter((task) => !q || task.name.toLowerCase().includes(q)).slice(0, 8);
}

function labelize(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

interface NamedProposalShortcut {
  label: string;
  id: string;
  name: string;
  secondary?: string;
}

function findNamedProposalShortcuts(
  proposals: Proposal[],
  recents: RecentItem[],
): NamedProposalShortcut[] {
  const targets = [
    { label: 'Open Jessee proposal', needle: 'jessee heating' },
    { label: 'Open Torres proposal', needle: 'torres structural' },
  ];
  const out: NamedProposalShortcut[] = [];
  for (const target of targets) {
    const proposal = proposals.find((item) =>
      item.proposalName.toLowerCase().includes(target.needle),
    );
    if (proposal) {
      out.push({
        label: target.label,
        id: proposal.id,
        name: proposal.proposalName,
        secondary: proposal.status,
      });
      continue;
    }
    const recent = recents.find(
      (item) => item.type === 'proposal' && item.name.toLowerCase().includes(target.needle),
    );
    if (recent) {
      out.push({
        label: target.label,
        id: recent.id,
        name: recent.name,
        secondary: recent.secondary,
      });
    }
  }
  return out;
}

function openRecent(item: RecentItem, navigate: (to: string) => void, close: () => void) {
  const pathByType: Record<RecentEntityType, string> = {
    task: `/tasks/${item.id}`,
    client: `/clients/${item.id}`,
    user: `/users/${item.id}`,
    website: `/websites/${item.id}`,
    proposal: `/proposals/${item.id}`,
  };
  pushRecent(item);
  navigate(pathByType[item.type]);
  close();
}

function iconForType(type: RecentEntityType) {
  if (type === 'task') return <CheckSquare className="h-4 w-4" />;
  if (type === 'client') return <Building2 className="h-4 w-4" />;
  if (type === 'user') return <User className="h-4 w-4" />;
  if (type === 'proposal') return <FileText className="h-4 w-4" />;
  return <Globe className="h-4 w-4" />;
}

function iconForCommand(label: string) {
  if (label.includes('new')) return <Plus className="h-4 w-4" />;
  if (label.includes('send')) return <Send className="h-4 w-4" />;
  if (label.includes('copy')) return <Copy className="h-4 w-4" />;
  if (label.includes('time')) return <Clock className="h-4 w-4" />;
  return <CheckSquare className="h-4 w-4" />;
}

function formatRate(value: number | undefined) {
  if (value === undefined) return undefined;
  return `$${value}/hr`;
}

interface CopilotPromptBlockProps {
  initialPrompt: string;
  onSubmit: (prompt: string) => void;
  taskContext?: string;
  proposalContext?: string;
}

const CopilotPromptBlock = forwardRef<HTMLTextAreaElement, CopilotPromptBlockProps>(
  function CopilotPromptBlock({ initialPrompt, onSubmit, taskContext, proposalContext }, ref) {
    const [value, setValue] = useState(initialPrompt);

    useEffect(() => {
      setValue(initialPrompt);
    }, [initialPrompt]);

    const submit = () => {
      const trimmed = value.trim();
      if (!trimmed) return;
      onSubmit(trimmed);
    };

    return (
      <div className="space-y-3 px-3 py-3">
        <div className="flex items-center gap-2 text-[12px] font-semibold text-primary">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          Ask Alice
        </div>
        {(taskContext || proposalContext) && (
          <div className="rounded-md border border-border bg-muted/30 px-2.5 py-1.5 text-[11px] text-muted-foreground">
            Context: <span className="text-foreground">{taskContext ?? proposalContext}</span>
          </div>
        )}
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          rows={3}
          placeholder="What's on my plate today?"
          className="w-full resize-none rounded-md border border-input bg-background px-2.5 py-2 text-[13px] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground">⌘/Ctrl+Enter to send</span>
          <button
            type="button"
            onClick={submit}
            disabled={value.trim().length === 0}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" aria-hidden />
            Send
          </button>
        </div>
      </div>
    );
  },
);
